import React from 'react';
import {Map} from 'immutable';
import {buttonStyle} from './styles';
import {
  withMeta,
  Plugins,
  Atom,
  AttributesMap,
  StyleAttributeImm,
} from '@packages/systems/wfdl';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  DATA_ATTR_LOADING_TEXT,
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_ADD_TO_CART_BUTTON,
} from '@packages/systems/commerce/constants';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {
  getArrayOrElse,
  getInPojo,
  getNumberOrElse,
  getString,
} from '@packages/utilities/data-utils';

const DEFAULT_TEXT_KEY = 'DEFAULT';
const WAITING_TEXT_KEY = 'WAITING';

const textStates = {
  [DEFAULT_TEXT_KEY]: {
    id: DEFAULT_TEXT_KEY,
    name: 'Default text',
    copy: 'Add to Cart',
  },
  [WAITING_TEXT_KEY]: {
    id: WAITING_TEXT_KEY,
    name: 'Waiting text',
    copy: 'Adding to cart...',
  },
} as const;

const CommerceAddToCartButton = Atom.createRender((props) => {
  const {
    attributes,
    classes,
    generateAttributes,
    context,
    data,
    environmentContext,
  } = props;

  const texts = getArrayOrElse(getInPojo(data, ['texts']), []);
  const index = getNumberOrElse(getInPojo(data, ['temp', 'index']), 0);
  const loadingText = getString(getInPojo(texts, [texts.length - 1, 'copy']));
  const value = getString(getInPojo(texts, [Number(index), 'copy']));
  const ariaBusy = index !== 0;

  const attributesToMerge: {[key: string]: string | StyleAttributeImm}[] = [
    {
      [DATA_ATTR_NODE_TYPE]: NODE_TYPE_COMMERCE_ADD_TO_CART_BUTTON,
    },
  ];
  if (typeof loadingText === 'string') {
    attributesToMerge.push({[DATA_ATTR_LOADING_TEXT]: loadingText});
  }
  if (typeof value === 'string') {
    attributesToMerge.push({value});
  }
  attributesToMerge.push(
    {'aria-busy': ariaBusy ? 'true' : 'false'},
    {'aria-haspopup': 'dialog'}
  );

  let newAttributes: AttributesMap = attributes.merge(...attributesToMerge);
  if (!context.get('showAddToCartButton')) {
    newAttributes = newAttributes.merge(Map({style: Map({display: 'none'})}));
  }

  const customNodeState = environmentContext.get('customNodeState');
  let updatedClasses = classes;
  if (
    customNodeState &&
    customNodeState.includes('ecommerce-add-to-cart-disabled')
  ) {
    updatedClasses = updatedClasses.push(`w--ecommerce-add-to-cart-disabled`);
  }

  return <input {...generateAttributes(newAttributes, updatedClasses)} />;
});

const config = Atom.create({
  displayName: 'Add to Cart Button',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Button')),
  },
  showEmpty: false,
  constraints: {
    ancestors: [
      {
        is: 'CommerceAddToCartForm',
        rule: 'ExactlyOne',
      },
    ],
  },
  style: {
    base: {
      // This FlowFixMe is here because ecommerce-add-to-cart-disabled isn't technically a pseudo, but a custom node state

      main: {
        noPseudo: buttonStyle,
        // @ts-expect-error - TS2322 - Type '{ noPseudo: WebflowCssProperties; 'ecommerce-add-to-cart-disabled': { color: tinycolor.ColorFormats.RGBA; backgroundColor: tinycolor.ColorFormats.RGBA; ... 5 more ...; outlineStyle: string; }; }' is not assignable to type 'Partial<Record<"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within", WebflowCssProperties>>'.
        'ecommerce-add-to-cart-disabled': {
          color: createRgbaColor('#666666'),
          backgroundColor: createRgbaColor('#e6e6e6'),
          borderTopColor: createRgbaColor('#e6e6e6'),
          borderBottomColor: createRgbaColor('#e6e6e6'),
          borderLeftColor: createRgbaColor('#e6e6e6'),
          borderRightColor: createRgbaColor('#e6e6e6'),
          cursor: 'not-allowed',
          outlineStyle: 'none',
        },
      },
    },
  },
  render: CommerceAddToCartButton,
  dataType: withMeta(
    {
      textStates,
    },
    createIntegratedRootAliasInstance(
      qualify(
        Plugins.name('Commerce'),
        typeName('AddToCartButtonMultiTextEdit')
      )
    )
  ),
});

export default config;
